import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  Switch,
  notification,
} from "antd";
import React, { useEffect, useState } from "react";
import SunEditor from "suneditor-react";
import {
  align,
  font,
  fontColor,
  fontSize,
  formatBlock,
  hiliteColor,
  horizontalRule,
  lineHeight,
  list,
  paragraphStyle,
  table,
  template,
  textStyle,
  image,
  link,
  video,
} from "suneditor/src/plugins";
import "suneditor/dist/css/suneditor.min.css";
import "suneditor/src/assets/css/suneditor-contents.css";
import { useAuth } from "../../../context/auth";

const ActionModal = ({
  visible,
  modalType,
  editRecord,
  onCancel,
  onOk,
}) => {
  const { accessToken } = useAuth();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const [editorStateEn, setEditorStateEn] = useState("");
  const [editorStateTh, setEditorStateTh] = useState("");
  const optionEditor = {
    showPathLabel: false,
    minHeight: "20vh",
    placeholder: "Enter Display Detail here",
    plugins: [
      align,
      font,
      fontColor,
      fontSize,
      formatBlock,
      hiliteColor,
      horizontalRule,
      lineHeight,
      list,
      paragraphStyle,
      table,
      template,
      textStyle,
      image,
      link,
      video,
    ],
    buttonList: [
      ["undo", "redo"],
      ["font", "fontSize", "formatBlock"],
      [
        "bold",
        "underline",
        "italic",
        "strike",
        "subscript",
        "superscript",
        "removeFormat",
      ],
      [
        "fontColor",
        "hiliteColor",
        "outdent",
        "indent",
        "align",
        "horizontalRule",
        "list",
        "table",
      ],
      ["link", "image", "video"],
    ],
    formats: ["p", "div", "h1", "h2", "h3", "h4", "h5", "h6"],
    font: [
      "Arial",
      "Georgia",
      "Tahoma",
      "Trebuchet MS",
      "Verdana",
      "Prompt",
      "Kanit",
      "Sarabun",
      "Pridi",
      "NotoSansThai",
    ],
  };

  useEffect(() => {
    if (!editRecord) return;

    async function getPage(id) {
      const url = new URL('/api/page/' + id, window.location);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { page } = await res.json();
        form.setFieldsValue(page);
        setEditorStateEn(page.detailEn);
        setEditorStateTh(page.detailTh);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

    };

    if (modalType === 'new') {
      setEditorStateEn("");
      setEditorStateTh("");
      form.resetFields();
    } else {
      getPage(editRecord.id);
    }
  }, [modalType, editRecord, accessToken, form]);

  return (
    <>
      <Modal
        open={visible}
        title={
          modalType === "new" ? (
            <div className="flex items-center">
              <PlusOutlined />
              <span>New Privacy Policy</span>
            </div>
          ) : (
            <div className="flex items-center space-x-4">
              <EditOutlined />
              <span>Edit Privacy Policy</span>
            </div>
          )
        }
        width={window.innerWidth <= 1000 ? "95%" : "65%"}
        onCancel={onCancel}
        footer={
          <div className="w-full text-center mb-4 flex justify-center">
            <Button
              className="w-36"
              type="primary"
              ghost
              key="cancel"
              shape="round"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              onClick={() => form.submit()}
              className="w-36"
              key="save"
              type="primary"
              shape="round"
              loading={loading}
            >
              Save
            </Button>
          </div>
        }
      >
        <Spin spinning={loading} tip="Loading...">
          <Form
            form={form}
            onFinish={async (values) => {
              setLoading(true);

              values.type = 'privacy';
              values.detailEn = editorStateEn;
              values.detailTh = editorStateTh;

              const url = new URL(modalType === 'new' ? '/api/page' : '/api/page/' + editRecord.id, window.location);

              const res = await fetch(url, {
                headers: {
                  'X-ACCESS-TOKEN': accessToken,
                  'Content-type': 'application/json',
                },
                method: modalType === 'new' ? 'POST' : 'PATCH',
                body: JSON.stringify(values),
              });
              if (res.ok) {
                notification["success"]({
                  message: "Successfully saved data.",
                });
                onOk();
              } else {
                notification["error"]({
                  message: "Something went wrong !",
                  description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                });
              }

              setLoading(false);
            }}
            layout="vertical"
          >
            <Row>
              <Col span="12">
                <Form.Item
                  label="Title (EN)"
                  name="titleEn"
                  rules={[
                    {
                      required: true,
                      message: "Title (EN) is required.",
                    },
                  ]}
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    maxLength={255}
                    placeholder="Title (EN)"
                  />
                </Form.Item>
              </Col>
              <Col span="24">
                <Form.Item>
                  <SunEditor
                    setContents={editorStateEn}
                    onChange={(html) => setEditorStateEn(html)}
                    setOptions={optionEditor}
                  />
                </Form.Item>
              </Col>
              <Col span="12">
                <Form.Item
                  label="Title (TH)"
                  name="titleTh"
                  rules={[
                    {
                      required: true,
                      message: "Title (TH) is required.",
                    },
                  ]}
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    maxLength={255}
                    placeholder="Title (TH)"
                  />
                </Form.Item>
              </Col>
              <Col span="24">
                <Form.Item>
                  <SunEditor
                    setContents={editorStateTh}
                    onChange={(html) => setEditorStateTh(html)}
                    setOptions={optionEditor}
                  />
                </Form.Item>
              </Col>
              <Col span="24">
                <Form.Item
                  label="Active"
                  name="active"
                  colon={false}
                  initialValue={false}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default ActionModal;
